import React from 'react';
// nodejs library that concatenates classes
import classNames from 'classnames';
// nodejs library to set properties for components
import PropTypes from 'prop-types';

// @mui/material components
import { makeStyles } from '@mui/styles';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Grow from '@mui/material/Grow';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Popper from '@mui/material/Popper';
import { useMediaQuery, useTheme } from '@mui/material';

// core components
import Button from 'components/CustomButtons/Button.js';

import styles from 'assets/jss/material-kit-react/components/customDropdownStyle.js';

const useStyles = makeStyles(styles);

export default function CustomDropdown(props) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:900px)');
  // Store the position data when calculating
  const [positionData, setPositionData] = React.useState({
    isLeftSide: false,
    isRightSide: false,
    isCenterPosition: false,
    isWideAnchor: false,
    isTopRow: false,
    isBottomRow: false,
  });

  // Calculate the real position of the anchor element in the viewport
  const calculatePosition = (element) => {
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const navElement = element.closest('header') || element.closest('nav');

    let navTop = 0;
    let navBottom = viewportHeight;

    // Get the navigation container bounds if available
    if (navElement) {
      const navRect = navElement.getBoundingClientRect();
      navTop = navRect.top;
      navBottom = navRect.bottom;
    }

    // Determine if element is in the top, middle, or bottom third of the nav
    const isTopRow = rect.top < navTop + (navBottom - navTop) / 3;
    const isBottomRow = rect.bottom > navBottom - (navBottom - navTop) / 3;

    // Horizontal position calculations
    const isLeftSide = rect.left < viewportWidth * 0.28;
    const isRightSide = rect.right > viewportWidth * 0.75;
    const isCenterPosition = !isLeftSide && !isRightSide;
    const isWideAnchor = rect.width > 700;

    return {
      isLeftSide,
      isRightSide,
      isCenterPosition,
      isWideAnchor,
      isTopRow,
      isBottomRow,
      rect,
    };
  };

  const handleMouseEnter = (event) => {
    if (!isMobile) {
      const target = event.currentTarget;
      setAnchorEl(target);
      // Calculate position data when opening
      setPositionData(calculatePosition(target) || {});
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setOpen(false);
      setAnchorEl(null);
    }
  };

  const handleClick = (event) => {
    if (isMobile) {
      setAnchorEl(event.currentTarget);
      setOpen(!open);
    }
  };

  const handleClose = (param) => {
    if (props && props.onClick) {
      props.onClick(param);
    }
  };

  const handleCloseAway = (event) => {
    if (anchorEl && anchorEl.contains(event.target)) {
      return;
    }
    setOpen(false);
    setAnchorEl(null);
  };

  const classes = useStyles();
  const {
    buttonText,
    buttonIcon,
    dropdownList,
    buttonProps,
    dropup,
    dropdownHeader,
    caret,
    hoverColor,
    left,
    rtlActive,
    noLiPadding,
    menuProps,
  } = props;

  const caretClasses = classNames({
    [classes.caret]: true,
    [classes.caretActive]: open,
    [classes.caretRTL]: rtlActive,
  });

  const dropdownItem = classNames({
    [classes.dropdownItem]: true,
    [classes[hoverColor + 'Hover']]: true,
    [classes.noLiPadding]: noLiPadding,
    [classes.dropdownItemRTL]: rtlActive,
  });

  let icon = null;
  switch (typeof buttonIcon) {
    case 'object':
      icon = <props.buttonIcon className={classes.buttonIcon} />;
      break;
    case 'string':
      icon = <Icon className={classes.buttonIcon}>{props.buttonIcon}</Icon>;
      break;
    default:
      icon = null;
      break;
  }

  return (
    <div onMouseLeave={handleMouseLeave}>
      <div>
        <Button
          aria-label="Notifications"
          aria-owns={open ? 'menu-list' : null}
          aria-haspopup="true"
          {...buttonProps}
          onMouseEnter={handleMouseEnter}
          onClick={handleClick}
          className={typeof buttonProps?.className === 'string' ? buttonProps.className : ''}
        >
          {icon}
          {buttonText !== undefined ? buttonText : null}
          {caret ? <b className={caretClasses} /> : null}
        </Button>
      </div>
      <Popper
        open={open}
        anchorEl={anchorEl}
        transition
        disablePortal
        placement={dropup ? 'top-start' : 'bottom-start'}
        className={classNames({
          [classes.popperClose]: !open,
          [classes.popperResponsive]: true,
          ['leftPosition']: !isMobile && positionData.isLeftSide,
          ['rightPosition']: !isMobile && positionData.isRightSide,
          ['centerPosition']: !isMobile && positionData.isCenterPosition,
          ['wideAnchor']: !isMobile && positionData.isWideAnchor,
          ['topRowPosition']: positionData.isTopRow,
          ['bottomRowPosition']: positionData.isBottomRow,
        })}
        {...menuProps}
        onEntering={(element) => {
          if (menuProps && typeof menuProps.onEntering === 'function') {
            menuProps.onEntering(element);
          }
        }}
      >
        {() => (
          <Grow
            in={open}
            id="menu-list"
            style={dropup ? { transformOrigin: '0 100% 0' } : { transformOrigin: '0 0 0' }}
          >
            <Paper className={classes.dropdown}>
              <ClickAwayListener onClickAway={handleCloseAway}>
                <MenuList role="menu" className={classes.menuList}>
                  {dropdownHeader !== undefined ? (
                    <MenuItem
                      onClick={() => handleClose(dropdownHeader)}
                      className={classes.dropdownHeader}
                    >
                      {dropdownHeader}
                    </MenuItem>
                  ) : null}
                  {dropdownList.map((prop, key) => {
                    if (prop.divider) {
                      return (
                        <Divider
                          key={key}
                          onClick={() => handleClose('divider')}
                          className={classes.dropdownDividerItem}
                        />
                      );
                    }
                    return (
                      <MenuItem
                        key={key}
                        onClick={() => handleClose(prop)}
                        className={dropdownItem}
                      >
                        {prop}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
}

CustomDropdown.defaultProps = {
  caret: true,
  hoverColor: 'warning',
  menuProps: {},
};

CustomDropdown.propTypes = {
  hoverColor: PropTypes.oneOf(['black', 'primary', 'info', 'success', 'warning', 'danger', 'rose']),
  buttonText: PropTypes.node,
  buttonIcon: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  dropdownList: PropTypes.array,
  buttonProps: PropTypes.object,
  dropup: PropTypes.bool,
  dropdownHeader: PropTypes.node,
  rtlActive: PropTypes.bool,
  caret: PropTypes.bool,
  left: PropTypes.bool,
  noLiPadding: PropTypes.bool,
  onClick: PropTypes.func,
  menuProps: PropTypes.object,
};
